import type { FC, ReactNode } from "react";

import { getBuilderImageResizeUrl } from "@ui/utils/getBuilderImageResizeUrl";
import { getOptimizedBackgroundImage } from "@ui/utils/getOptimizedBackgroundImage";
import classNames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";
import { Button } from "@jti/core-components/components/Button";

import { Typography } from "../Typography";

import styles from "./DiscreteHeaderBanner.module.scss";

export type DiscreteHeaderBannerTitleAs = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export type DiscreteHeaderBannerProps = {
  title: ReactNode;
  titleAs?: DiscreteHeaderBannerTitleAs;
  description?: string;
  backgroundImage?: string;
  icon?: DirectionalIconComponent;
  linkLabel?: string;
  linkHref?: string;
  placeholder?: ReactNode;
  theme?: "dark" | "light";
  dataTestId?: string;
};

export const DiscreteHeaderBanner: FC<DiscreteHeaderBannerProps> = ({
  title,
  titleAs = "h2",
  description,
  linkLabel,
  linkHref,
  placeholder,
  icon: Icon,
  backgroundImage,
  theme = "dark",
  dataTestId,
}) => {
  const isLinkVisible = linkLabel && linkHref;
  const backgroundResizedImageUrl = getBuilderImageResizeUrl({ imageUrl: backgroundImage });
  const optimizedBackgroundImage = backgroundResizedImageUrl
    ? getOptimizedBackgroundImage(backgroundResizedImageUrl)
    : undefined;

  return (
    <div
      className={classNames(styles.content, `${theme}-theme`, {
        [styles.oldImage]: !backgroundImage,
      })}
      style={{ "--background-image": optimizedBackgroundImage }}
      data-testid={dataTestId}
    >
      {Icon && <div className={classNames(styles.icon, styles.desktopIcon)}>{<Icon />}</div>}
      <div className={styles.textWrapper}>
        {Icon && <div className={classNames(styles.icon, styles.mobileIcon)}>{<Icon />}</div>}
        <Typography as={titleAs} variant="h1" className={styles.title}>
          {title}
        </Typography>
        {description && <p className={styles.description}>{description}</p>}
        {isLinkVisible && (
          <Button href={linkHref} size="lg" variant="text" target="_blank" nativeBehaviour>
            {linkLabel}
          </Button>
        )}
      </div>
      {placeholder}
    </div>
  );
};
